import { pageTitle } from '@palaccount/config';
import { Plus } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { Gated } from '@/components/permission-gate';
import { Pager } from '@/components/pager';
import { DocStatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePagedState, usePageRange } from '@/lib/paging';

import { useCashTransfers } from './api';
import { BankingTabs } from './banking-tabs';

/**
 * FIN-06 — the Cash Transfer register: number, date, the two accounts, the
 * amount, and the lifecycle chip (Draft grey / Posted green with its lock /
 * Cancelled red).
 */
export function TransfersListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  /** NFR-42 — the page rides in the URL. */
  const paging = usePagedState();
  const { data, isPending, isError } = useCashTransfers(paging.page, paging.pageSize);
  const transfers = data?.transfers;
  const range = usePageRange(data, paging.pageSize);

  useEffect(() => {
    document.title = pageTitle(t('banking.transfersTitle'));
  }, [t]);

  return (
    <div className="grid gap-4">
      <BankingTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('banking.transfersTitle')}</h2>
        <Gated permission="banking.create" reasonKey="perm.cannot.createCashTransfers">
          <Button asChild>
            <Link to="/banks/transfers/new">
              <Plus aria-hidden /> {t('common.new')}
            </Link>
          </Button>
        </Gated>
      </div>
      <Card>
        <CardContent>
          {isPending ? (
            <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
          ) : isError ? (
            <p role="alert" className="py-8 text-center text-sm text-destructive">
              {t('common.loadFailed')}
            </p>
          ) : (transfers ?? []).length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {t('banking.transfersEmpty')}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('banking.colNo')}</TableHead>
                  <TableHead>{t('common.date')}</TableHead>
                  <TableHead>{t('banking.colFrom')}</TableHead>
                  <TableHead>{t('banking.colTo')}</TableHead>
                  <TableHead className="text-end">{t('banking.colAmount')}</TableHead>
                  <TableHead>{t('banking.colStatus')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(transfers ?? []).map((transfer) => (
                  <TableRow
                    key={transfer.id}
                    className="cursor-pointer"
                    onClick={() => void navigate(`/banks/transfers/${transfer.id}`)}
                  >
                    <TableCell>
                      <Ltr value={transfer.code} className="font-mono text-muted-foreground" />
                    </TableCell>
                    <TableCell>
                      <Ltr value={transfer.transferDate} className="font-mono" />
                    </TableCell>
                    <TableCell className="text-sm">{transfer.fromAccountName}</TableCell>
                    <TableCell className="text-sm">{transfer.toAccountName}</TableCell>
                    <TableCell className="text-end">
                      <Amount value={transfer.amount} currency={transfer.fromCurrency} />
                    </TableCell>
                    <TableCell>
                      <DocStatusChip status={transfer.status} postedTone="green" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* NFR-42 — the range, the total, and the two steps. */}
          {!isPending && !isError && (data?.total ?? 0) > 0 ? (
            <Pager
              page={paging.page}
              pages={range.pages}
              first={range.first}
              last={range.last}
              total={range.total}
              onPage={paging.setPage}
              label={t('banking.transfersTitle')}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
